import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
// Manager-only review surfaces (admins bypass). Personal surfaces show for all.
const REVIEW_ADMIN_ROLES = ["admin", "srmanager", "manager", "creativedirector"];
import { useState, useEffect } from "react";
import { sounds, burst, showToast, addXP, addCombo } from "../hooks/useDMEngine";
import { isSuperAdmin } from "../hooks/useSuperAdmin";

const roleKey = (r) => (r || "").toLowerCase();

const NAV_SECTIONS = [
  {
    label: null, items: [
      { icon: "⌂", label: "MY DAY", path: "/me" },
    ]
  },
  {
    label: null, items: [
      { icon: "◈", label: "LEADS", path: "/bizdev/leads" },
      { icon: "◻", label: "BRIEFS", path: "/bizdev/briefs" },
      { icon: "▦", label: "CLIENTS", path: "/bizdev/accounts" },
    ]
  },
  {
    label: null, items: [
      { icon: "☐", label: "TEAM TASKS", path: "/tasks" },
      { icon: "▣", label: "NON-CLIENT PROJECTS", path: "/projects" },
      { icon: "🎬", label: "PRODUCTION", path: "/production" },
      { icon: "🔀", label: "ROUTING", path: "/routing" },
      { icon: "✍️", label: "SCRIPTS", path: "/scripts" },
      { icon: "📑", label: "LIBRARY", path: "/pitch-decks" },
      { icon: "🎓", label: "ONBOARDING", path: "/onboarding" },
      { icon: "🧑‍💼", label: "RECRUITING", path: "/recruiting" },
      // bug_7412e0f449fb — ops requests routed to Mukesh.
      // Visible to everyone (anyone can file + view their own).
      { icon: "📋", label: "ADMIN TASKS", path: "/admin/tasks" },
    ]
  },
  {
    label: "PRODUCTION TRACKER", items: [
      { icon: "⌂", label: "MY DAY", path: "/ptracker/myday" },
      { icon: "◱", label: "PIPELINE", path: "/ptracker/pipeline" },
      { icon: "✓", label: "MY QUEUE", path: "/ptracker/queue" },
      { icon: "☑", label: "DAILY TASKS", path: "/ptracker/tasks" },
      { icon: "🎬", label: "SHOOTS", path: "/ptracker/shoots" },
      { icon: "🗄", label: "STUDIO CALENDAR", path: "/ptracker/calendar" },
      { icon: "📈", label: "PRODUCTIVITY", path: "/ptracker/productivity", roles: REVIEW_ADMIN_ROLES },
      { icon: "🗓", label: "ATTENDANCE", path: "/ptracker/attendance" },
      { icon: "👥", label: "TEAM ALLOCATION", path: "/ptracker/allocation", roles: REVIEW_ADMIN_ROLES },
      { icon: "⚠", label: "RISKS", path: "/ptracker/risks", roles: REVIEW_ADMIN_ROLES },
      { icon: "⚙", label: "TRACKER SETTINGS", path: "/ptracker/settings", roles: ["admin"] },
    ]
  },
  {
    label: "REVIEWS", items: [
      { icon: "📊", label: "MY REVIEWS", path: "/reviews" },
      { icon: "📥", label: "REVIEW INBOX", path: "/reviews/inbox" },
      { icon: "🏷️", label: "RETAINER & CAMPAIGN LOG", path: "/reviews/work-log", roles: REVIEW_ADMIN_ROLES },
      { icon: "🩺", label: "RETAINER HEALTH", path: "/reviews/retainer-health", roles: REVIEW_ADMIN_ROLES },
      { icon: "🗂️", label: "REVIEW ADMIN", path: "/reviews/admin", roles: REVIEW_ADMIN_ROLES },
    ]
  },
  {
    label: "RESOURCES", items: [
      { icon: "🔑", label: "MICROSITES & PASSWORDS", path: "/microsites" },
    ]
  },
];

export default function AppSidebar({ open, onClose }) {
  const { user, logout } = useAuth();
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  // Apply theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('theme', next);
  };
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = isSuperAdmin(user?.email) || roleKey(user?.role) === "admin";
  const modules = user?.module_access || [];
  const isActive = (path) => {
    if (path === "/me") return location.pathname === "/me";
    return location.pathname.startsWith(path);
  };
  const handleNav = (path) => {
    sounds.resume();
    sounds.blip();
    navigate(path);
    if (onClose) onClose();
  };
  const sidebarStyle = {
    position: 'fixed', top: 0, left: 0, bottom: 0,
    width: 'var(--sidebar-width)', background: 'var(--s1)',
    borderRight: '1px solid var(--bdr)',
    flexDirection: 'column', zIndex: 80,
    transition: 'transform .25s cubic-bezier(.4,0,.2,1)',
    animation: 'dmSlideInLeft .5s cubic-bezier(.34,1.56,.64,1) both',
  };
  return (
    <>
      {open && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 79 }} className="lg:hidden" />
      )}
      <aside style={sidebarStyle} className={`hidden lg:flex dm-sidebar ${open ? '!flex' : ''}`}>
        <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid var(--bdr)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, background: 'var(--rd)', border: '1px solid rgba(227,25,55,.18)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-orb)', fontSize: 12, color: 'var(--r)', fontWeight: 900 }}>DM</div>
            <div>
              <div style={{ fontFamily: 'var(--font-orb)', fontSize: 11, fontWeight: 700, color: 'var(--t1)', letterSpacing: '.08em', lineHeight: 1 }}>DREAM MACHINE</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t3)', letterSpacing: '.12em', marginTop: 3 }}>DENTSU AMP · OS</div>
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', background: 'var(--rd)', border: '1px solid rgba(227,25,55,.12)', borderRadius: 4 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--r)', animation: 'dmPip 2s ease-in-out infinite' }} id="dm-chip-notif" />

          </div>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 8px' }}>
          {NAV_SECTIONS.map((section, si) => {
            if (section.module && !isAdmin && !modules.includes(section.module)) return null;
            // Email allowlist gate (pilot features) — NO admin bypass.
            if (section.emails && !section.emails.some(e => e.toLowerCase() === (user?.email || "").toLowerCase())) return null;
            return (
              <div key={si} style={{ marginBottom: 6 }}>
                {section.label && (
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t4)', letterSpacing: '.14em', padding: '10px 10px 4px', userSelect: 'none' }}>{section.label}</div>
                )}
                {section.items.filter((item) => {
                  if (!item.roles) return true;
                  if (isAdmin) return true;
                  return item.roles.includes(roleKey(user?.role));
                }).map((item) => {
                  const active = isActive(item.path);
                  return (
                    <div key={item.path} onClick={() => handleNav(item.path)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'all .15s', background: active ? 'var(--rd)' : 'transparent', borderLeft: active ? '2px solid var(--r)' : '2px solid transparent', marginBottom: 1 }}
                      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.02)'; }}
                      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                      <div style={{ width: 5, height: 5, borderRadius: '50%', background: active ? 'var(--r)' : 'var(--t4)', boxShadow: active ? '0 0 6px var(--r)' : 'none', transition: 'all .2s' }} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '.08em', color: active ? 'var(--r)' : 'var(--t2)', fontWeight: active ? 600 : 400 }}>{item.label}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {isAdmin && (
            <>
              {[{l:"ADMIN",p:"/admin"},{l:"BUG REPORTS",p:"/admin/bugs"}].map(item => {
                const active = location.pathname === item.p;
                return (
                <div key={item.p} onClick={() => handleNav(item.p)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', background: active ? 'var(--rd)' : 'transparent', borderLeft: active ? '2px solid var(--r)' : '2px solid transparent', marginBottom: 1 }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.02)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: active ? 'var(--r)' : 'var(--t4)' }} />
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '.08em', color: active ? 'var(--r)' : 'var(--t2)' }}>{item.l}</span>
                </div>
              )})}
            </>
          )}
        </nav>
        <div style={{ padding: '12px 10px', borderTop: '1px solid var(--bdr)' }}>
          <div onClick={() => handleNav("/me")} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'background .15s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.02)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ width: 24, height: 24, borderRadius: 4, background: 'var(--rd)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-orb)', fontSize: 12, color: 'var(--r)', fontWeight: 700 }}>{(user?.full_name || user?.name || "?")[0]?.toUpperCase()}</div>
            <div>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'var(--t1)', fontWeight: 500, lineHeight: 1 }}>{user?.full_name || user?.name || "User"}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--t3)', letterSpacing: '.06em', marginTop: 2 }}>{user?.designation || roleKey(user?.role)}</div>
            </div>
          </div>
          <div onClick={toggleTheme} title={theme === 'dark' ? 'Switch to Unc Mode' : 'Switch to Punk Mode'} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', marginBottom: 2, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', letterSpacing: '.06em' }}>
            {theme === 'dark' ? '☀' : '◑'} {theme === 'dark' ? 'UNC MODE' : 'PUNK MODE'}
          </div>
          <div onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 'var(--radius-sm)', cursor: 'pointer', marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--t3)', letterSpacing: '.06em' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--r)'; e.currentTarget.style.background = 'var(--rd)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--t3)'; e.currentTarget.style.background = 'transparent'; }}>
            ⏻ SIGN OUT
          </div>
        </div>
      </aside>
      <style>{`
        @media (max-width: 1023px) {
          aside { transform: translateX(-100%); }
          aside.\\!flex { transform: translateX(0) !important; display: flex !important; }
        }
      `}</style>
    </>
  );
}
